const __DEV__ = false;

import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { createPopper } from '@popperjs/core';
import * as ReactDOM from 'react-dom';
import { getStyles, sanitizeProps } from '../../styles/theme/index.js';
import { S } from './styles.js';
import { jsx } from '@emotion/react';
import mergeDefaultProps from '../../utils/merge-default-props.js';
import getOwnerDoc from '../../utils/get-owner-doc.js';

var _excluded = ["anchorEl", "children", "closeOnClick", "closeOnClickOutside", "container", "dataTestId", "isOpen", "modifiers", "onClose", "placement", "popperOptions"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var defaultProps = {
  closeOnClick: false,
  closeOnClickOutside: true,
  isFullWidth: false,
  isOpen: false,
  modifiers: [],
  popperOptions: {},
  placement: 'bottom-end'
};
var getAnchorEl = function getAnchorEl(anchorEl) {
  if (!anchorEl) return null;
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl;
};
var PopoverInternal = function PopoverInternal(props, ref) {
  var propsInternal = mergeDefaultProps(props, defaultProps);
  var anchorEl = propsInternal.anchorEl,
      children = propsInternal.children,
      closeOnClick = propsInternal.closeOnClick,
      closeOnClickOutside = propsInternal.closeOnClickOutside,
      containerProp = propsInternal.container,
      _propsInternal$dataTe = propsInternal.dataTestId,
      dataTestId = _propsInternal$dataTe === void 0 ? 'popover' : _propsInternal$dataTe,
      isOpen = propsInternal.isOpen,
      modifiers = propsInternal.modifiers,
      onClose = propsInternal.onClose,
      placement = propsInternal.placement,
      popperOptions = propsInternal.popperOptions,
      rest = _objectWithoutProperties(propsInternal, _excluded);
  var tooltipRef = React.useRef({});
  var popperRef = React.useRef(null);
  var anchorElRef = getAnchorEl(anchorEl);
  React.useImperativeHandle(ref, function () {
    return _objectSpread(_objectSpread({}, tooltipRef.current), {}, {
      popper: popperRef.current
    });
  }, []);
  React.useEffect(function () {
    if (popperRef.current) {
      popperRef.current.forceUpdate();
    }
  });
  var handleOpen = React.useCallback(function () {
    if (!tooltipRef.current || !anchorElRef || !isOpen) {
      return;
    }
    if (popperRef.current) {
      popperRef.current.destroy();
    }
    if (!anchorElRef) return;
    var popperModifiers = [{
      name: 'offset',
      options: {
        offset: [0, 4]
      }
    }, {
      name: 'preventOverflow',
      options: {
        enabled: true,
        altBoundary: true,
        rootBoundary: 'document'
      }
    }, {
      name: 'flip',
      options: {
        altBoundary: true,
        rootBoundary: 'document',
        enabled: true
      }
    }];
    if (modifiers != null) {
      popperModifiers = popperModifiers.concat(modifiers);
    }
    if (popperOptions && popperOptions.modifiers != null) {
      popperModifiers = popperModifiers.concat(popperOptions.modifiers);
    }
    var popper = createPopper(anchorElRef, tooltipRef.current, _objectSpread(_objectSpread({
      placement: placement
    }, popperOptions), {}, {
      modifiers: popperModifiers
    }));
    popperRef.current = popper;
    return function () {
      popper.destroy();
      if (popperRef.current) {
        popperRef.current = null;
      }
    };
  }, [anchorElRef, isOpen, modifiers, placement, popperOptions]);
  var handleRef = React.useCallback(function (node) {
    tooltipRef.current = node;
    handleOpen();
  }, [handleOpen]);
  var handleClickListener = React.useCallback(function (e) {
    var _tooltipRef$current, _tooltipRef$current$c, _anchorElRef$contains;
    var clickedElement = e.target;
    var isClickOutsideTooltip = clickedElement !== tooltipRef.current && !((_tooltipRef$current = tooltipRef.current) !== null && _tooltipRef$current !== void 0 && (_tooltipRef$current$c = _tooltipRef$current.contains) !== null && _tooltipRef$current$c !== void 0 && _tooltipRef$current$c.call(_tooltipRef$current, clickedElement));
    if (closeOnClickOutside && isClickOutsideTooltip && !(anchorElRef !== null && anchorElRef !== void 0 && (_anchorElRef$contains = anchorElRef.contains) !== null && _anchorElRef$contains !== void 0 && _anchorElRef$contains.call(anchorElRef, clickedElement)) || closeOnClick && !isClickOutsideTooltip) {
      onClose && onClose(e);
    }
  }, [anchorElRef, closeOnClick, closeOnClickOutside, onClose]);
  React.useEffect(function () {
    if (!isOpen) {
      if (popperRef.current) {
        popperRef.current.destroy();
        popperRef.current = null;
        return;
      }
    }
    window.addEventListener('click', handleClickListener);
    return function () {
      window.removeEventListener('click', handleClickListener);
    };
  }, [handleClickListener, isOpen]);
  var container = React.useMemo(function () {
    if (containerProp) {
      var containerToRender = typeof containerProp === 'function' ? containerProp() : containerProp;
      return containerToRender !== null && containerToRender !== void 0 ? containerToRender : document.body;
    }
    if (anchorElRef) {
      var anchorToRender = getOwnerDoc(anchorElRef).body;
      return anchorToRender !== null && anchorToRender !== void 0 ? anchorToRender : document.body;
    }
    return document.body;
  }, [anchorElRef, containerProp]);
  return isOpen && ReactDOM.createPortal(jsx("div", _extends({
    ref: handleRef,
    css: function css(theme) {
      return [S.container(theme, propsInternal), getStyles('container', theme, propsInternal)];
    },
    "data-testid": dataTestId
  }, sanitizeProps(rest)), children), container) || null;
};
PopoverInternal.displayName = 'PopoverInternal';
var Popover = Object.assign( React.forwardRef(PopoverInternal), {
  displayName: 'Popover'
});

export { Popover };
//# sourceMappingURL=popover.js.map
